import { I18nThemed } from 'interfaces/mixins';
import has from '@dojo/framework/core/has';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { DNode, LocalizedMessages } from '@dojo/framework/core/interfaces';
import { storeInject } from '@dojo/framework/stores/StoreInjector';
import Store from '@dojo/framework/stores/Store';
import { Bundle, Messages } from '@dojo/framework/i18n/i18n';
import Button from '@dojo/widgets/button';
import { publish } from 'pub-sub';
import { SiteMeta, userstore } from 'lcc-state';
import { LPWidget } from 'lcc-interfaces';

import css from '../css/lptoolcard.m.css';
import { LPWidget as WidgetKid } from './lpwidget';

export interface LPToolCardProperties {
	classes?: string[];
	name: string;
	element?: string;
	icon?: string;
	iconcomplete?: string;
	action?: string;
	heading?: string;
	deck?: string;
	button?: string;
	transition?: string;
	isNextStep?: boolean;
	kids?: LPWidget[];
	dynls: Map<string, Bundle<Messages>>;
	complete?: boolean;
	extra?: string | undefined;
	hide?: boolean | undefined;
}

interface RenderProps {
	action?: string;
	button: string;
	classes: string[];
	deck: string;
	element: string;
	heading: string;
	icon: string;
	iconcomplete?: string;
	isNextStep: boolean;
	kids: LPWidget[];
	name: string;
	transition?: string;
}

@theme(css)
@storeInject({
	name: 'lcc-state',
	paths: [['dynls']],
	getProperties: (store: Store<SiteMeta>) => {
		const { get, path } = store;
		return {
			dynls: get(path('dynls'))
		};
	}
})
export class LPToolCard extends I18nThemed<LPToolCardProperties> {
	private _storehandle: any = undefined;

	protected onDetach(): void {
		this._storehandle && this._storehandle.remove();
	}

	@beforeProperties()
	ensure_toolstate(properties: LPToolCardProperties): LPToolCardProperties {
		const { get, path } = userstore;
		const { complete, extra, hide } = get(path('toolstate', properties.action as string)) || {
			complete: false,
			extra: undefined,
			hide: undefined
		};
		properties.complete = complete;
		properties.extra = extra;
		properties.hide = hide;

		if (this._storehandle === undefined) {
			this._storehandle = userstore.onChange(path('toolstate', properties.action as string), () => {
				this.invalidate();
			});
		}
		return properties;
	}

	protected get_nls(): LocalizedMessages<Messages> {
		const { dynls } = this.properties;
		const bundle = dynls.get('settings');
		if (!bundle) {
			return { isPlaceholder: true, format: () => '', messages: {} as Messages };
		}
		return this.localizeBundle(bundle);
	}

	protected render_nextstep(props: RenderProps): DNode {
		const { action, button, classes, element, kids, name } = props;
		return v(
			element,
			{
				key: name,
				role: 'menuitem',
				tabIndex: 0,
				classes: [this.theme(css.nextstepCard), ...classes],
				styles: {},
				xqa: `lptoolcard ${name}`
			},
			[
				...kids.map((val) => w(WidgetKid, val)),
				v('div', { classes: [this.theme(css.button)] }, [
					w(
						Button,
						{
							onClick: () => {
								const act = `site/${action || name}`;
								publish(act);
							}
						},
						[button]
					)
				])
			]
		);
	}

	protected render(): DNode {
		const { isPlaceholder, messages } = this.get_nls();
		if (isPlaceholder) {
			return null;
		}

		const {
			action,
			button = messages['lptoolcard_button'] || '',
			classes = [],
			deck = messages['lptoolcard_deck'] || '',
			element = 'div',
			heading = messages['lptoolcard_heading'] || '',
			icon = '',
			iconcomplete,
			isNextStep = false,
			kids = [],
			name,
			transition,
			complete,
			extra,
			hide
		} = this.properties;
		const specialNextStep = isNextStep && kids.length > 0 && has('use-alternate-next');

		let useicon = complete && iconcomplete ? iconcomplete : icon;
		// handle special case for countdown
		if (useicon === 'clock' && hide) {
			useicon = 'countdown';
		}

		const svgpath = `${window.hcstatic.portal_cdn}/js/images/iconsheet.svg#${useicon}`;

		return specialNextStep
			? this.render_nextstep({
					action,
					button,
					classes,
					deck,
					element,
					heading,
					icon,
					iconcomplete,
					isNextStep,
					kids,
					name,
					transition
			  })
			: v(
					element,
					{
						key: name,
						role: 'menuitem',
						tabIndex: 0,
						classes: [this.theme(isNextStep ? css.nextstepCard : css.root), ...classes],
						styles: isNextStep ? {} : { transform: transition },
						xqa: `lptoolcard ${name}`,
						onclick: (evt) => {
							evt && evt.stopPropagation();
							const act = `site/${action || name}`;
							publish(act);
						}
					},
					[
						v('svg', { 'xmlns:xlink': 'http://www.w3.org/1999/xlink', role: 'img' }, [
							v('use', { href: svgpath, 'xlink:href': svgpath })
						]),
						complete ? v('div', { classes: [this.theme(css.complete)] }, ['\u2713']) : null,
						extra && !hide ? v('div', { classes: [this.theme(css.extra)] }, [extra]) : null,
						v('div', { classes: [this.theme(css.textBlock)] }, [
							v('h2', { classes: [this.theme(css.heading)] }, [heading]),
							v('div', { classes: [this.theme(css.deck)] }, [deck])
						]),
						v('div', { classes: [this.theme(css.button)] }, [
							w(
								Button,
								{
									onClick: () => {
										const act = `site/${action || name}`;
										publish(act);
									}
								},
								[button]
							)
						])
					]
			  );
	}
}

export default LPToolCard;
